import { FC, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { SegmentPageLayout } from "../components/SegmentPageLayout";
import { ScoringProfileDialog } from "@/lib/scoring/components/ScoringProfileDialog";
import { ScoringWizardDialog } from "@/lib/scoring/components/ScoringWizardDialog";
import { ConfirmationDialog } from "@/components/Dialog/ConfirmationDialog";
import { useScoringStore } from "@/store/scoringStore";
import { getLoggedUserProfile, getTenantId } from "@/utils/auth";
import { openAiScoring } from "@/utils/module-urls";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import { useScoringMethodModal } from "@/hooks/useScoringMethodModal";
import {
  useCanManageScoring,
  MANAGE_SCORING_DISABLED_MESSAGE,
} from "@/hooks/useCanManageScoring";
import { useSnackbar } from "@/components/Snackbar";
import { useDialog } from "@/hooks/useDialog";
import { fetchSegments } from "@/apis/bacchus/segment";
import {
  getCriterionKey,
  CRITERION_LABELS,
} from "@/lib/scoring/criterionRegistry";
import { getBandRows } from "@/lib/scoring/utils";
import { format } from "date-fns";
import type { Segment } from "@/apis/bacchus/types";
import type { ScoringProfile, ScoringType } from "@/lib/scoring/types";

const formatDate = (date: string | undefined | null): string =>
  date ? format(new Date(date), "MMM d, yyyy") : "—";

const formatDateFull = (date: string | undefined | null): string =>
  date ? format(new Date(date), "MMM d, yyyy 'at' h:mm a") : "";

/** Human-readable label for a single scoring criterion entry. */
const criterionLabel = (criterion: ScoringType): string => {
  const key = getCriterionKey(criterion);
  return (key && CRITERION_LABELS[key]) || criterion.name || "Criterion";
};

/**
 * A criterion + its rule count + human-readable band summaries (e.g. "greater
 * than $1,000,000"). `ruleCount` is the raw number of configured bands; `bands`
 * uses the shared synchronous summariser so wording matches the editor.
 * ID-based criteria (industry, products…) may show counts rather than resolved
 * names since we don't run taxonomy lookups here.
 */
const summarizeCriterion = (criterion: ScoringType) => {
  const key = getCriterionKey(criterion);
  const rawBands = key ? ((criterion[key] as unknown[]) ?? []) : [];
  return {
    label: criterionLabel(criterion),
    ruleCount: rawBands.length,
    bands: getBandRows(criterion),
  };
};

/** Criterion chips with an inline rule count + band detail on hover. */
const CriteriaChips: FC<{ profile: ScoringProfile }> = ({ profile }) => {
  if (profile.parameters.criteria.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No criteria
      </Typography>
    );
  }
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={0.75}
      sx={{ maxWidth: 520, py: 0.5 }}
    >
      {profile.parameters.criteria.map((criterion, i) => {
        const { label, ruleCount, bands } = summarizeCriterion(criterion);
        return (
          <Tooltip
            key={i}
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: 360,
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 3,
                },
              },
              arrow: { sx: { color: "background.paper" } },
            }}
            title={
              bands.length === 0 ? (
                <Typography variant="caption">
                  {ruleCount} rule{ruleCount === 1 ? "" : "s"} (details
                  unavailable here)
                </Typography>
              ) : (
                <Stack gap={0.5} sx={{ py: 0.5 }}>
                  {bands.map((band, j) => (
                    <Chip
                      key={j}
                      size="small"
                      variant="outlined"
                      label={`${band.summary} · ${band.score} pts`}
                    />
                  ))}
                </Stack>
              )
            }
          >
            <Chip
              size="small"
              variant="outlined"
              data-testid={`scoring-profile-criterion-${profile.id}-${i}`}
              label={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <span>{label}</span>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      px: 0.5,
                      borderRadius: "8px",
                      bgcolor: "action.selected",
                      color: "text.secondary",
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {ruleCount}
                  </Box>
                </Box>
              }
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

/** Small label/value pair used inside the mobile card layout. */
const MetaRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ minWidth: 96, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Standalone manager for scoring profiles — create, edit and delete profiles
 * without first opening a segment. Reuses the same wizard + editor dialogs and
 * the shared scoring store that the Companies explore page uses; profiles are
 * user-scoped (fetched by email), so no segment is required.
 */
export const ScoringProfilesPage: FC = () => {
  const scoringEnabled = useFeatureFlag("maScoringProfilesManager", false);
  // Managing scoring config (create/edit/delete) is admin-only; other users
  // still see the list, but the write actions are disabled.
  const canManage = useCanManageScoring();
  const showMethodModal = useScoringMethodModal();
  const theme = useTheme();
  // Below `md` the table's six columns get cramped, so switch to a card grid.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const profiles = useScoringStore((s) => s.profiles);
  const isLoading = useScoringStore((s) => s.isLoading);
  const error = useScoringStore((s) => s.error);
  const fetchProfiles = useScoringStore((s) => s.fetchProfiles);
  const deleteProfile = useScoringStore((s) => s.deleteProfile);

  // V2 (argo model) scoring — read-only catalog listed alongside V1 profiles.
  const scoringV2Enabled = useFeatureFlag("maScoringModelsV2", false);
  const models = useScoringStore((s) => s.models);
  const fetchModels = useScoringStore((s) => s.fetchModels);

  const { showSuccess, showError } = useSnackbar();

  // Dialog state — mirrors the wiring in CompaniesExplorePage.
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ScoringProfile | null>(
    null
  );
  const [wizardOpen, setWizardOpen] = useState(false);
  const [generatedCriteria, setGeneratedCriteria] = useState<
    ScoringType[] | undefined
  >(undefined);
  const [generatedName, setGeneratedName] = useState<string | undefined>(
    undefined
  );

  const deleteDialog = useDialog<ScoringProfile>();
  const [isDeleting, setIsDeleting] = useState(false);

  const [search, setSearch] = useState("");

  // Segments only power the wizard's optional "generate from a segment" mode;
  // the products and manual paths need none.
  const [segments, setSegments] = useState<Segment[]>([]);

  useEffect(() => {
    if (!scoringEnabled) return;
    const email = getLoggedUserProfile()?.email;
    if (email) void fetchProfiles(email);
  }, [scoringEnabled, fetchProfiles]);

  useEffect(() => {
    if (!scoringEnabled) return;
    fetchSegments({ page: 1, pageSize: 100, filterBy: "my" })
      .then((res) => setSegments(res.data ?? []))
      .catch(() => setSegments([]));
  }, [scoringEnabled]);

  useEffect(() => {
    if (!scoringEnabled || !scoringV2Enabled) return;
    const tenantId = getTenantId();
    if (tenantId) void fetchModels(tenantId);
  }, [scoringEnabled, scoringV2Enabled, fetchModels]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...profiles]
      .filter(
        (p) =>
          !term ||
          p.name.toLowerCase().includes(term) ||
          (p.email ?? "").toLowerCase().includes(term)
      )
      .sort((a, z) => a.name.localeCompare(z.name));
  }, [profiles, search]);

  const openEdit = (profile: ScoringProfile) => {
    setEditingProfile(profile);
    setProfileDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    const profile = deleteDialog.data;
    if (!profile) return;
    setIsDeleting(true);
    try {
      await deleteProfile(profile.id);
      showSuccess(`Deleted "${profile.name}"`);
      deleteDialog.close();
    } catch {
      showError("Failed to delete scoring profile. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!scoringEnabled) {
    return <Navigate to="/segments" replace />;
  }

  const renderActions = (profile: ScoringProfile) => (
    <Tooltip title={canManage ? "" : MANAGE_SCORING_DISABLED_MESSAGE}>
      {/* span keeps the tooltip working while the buttons are disabled */}
      <span>
        <IconButton
          size="small"
          disabled={!canManage}
          onClick={() => openEdit(profile)}
          data-testid={`scoring-profile-edit-btn-${profile.id}`}
          aria-label={`Edit ${profile.name}`}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={!canManage}
          onClick={() => deleteDialog.open(profile)}
          data-testid={`scoring-profile-delete-btn-${profile.id}`}
          aria-label={`Delete ${profile.name}`}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  // Without the chooser, authoring goes straight to the admin AI-scoring tool;
  // fall back to the wizard if that URL can't be built.
  const handleCreate = () => {
    if (!showMethodModal && openAiScoring(getTenantId())) return;
    setWizardOpen(true);
  };

  return (
    <SegmentPageLayout
      title="Scoring Profiles"
      action={
        <Tooltip title={canManage ? "" : MANAGE_SCORING_DISABLED_MESSAGE}>
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!canManage}
              onClick={handleCreate}
              data-testid="scoring-new-profile-btn"
              sx={{ flexShrink: 0 }}
            >
              Create New Scoring Profile
            </Button>
          </span>
        </Tooltip>
      }
      toolbar={
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search scoring profiles..."
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { "data-testid": "scoring-profiles-search-input" },
          }}
          sx={{ width: { xs: "100%", sm: 320 } }}
        />
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && rows.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
          py={8}
          data-testid="scoring-profiles-empty"
        >
          <Typography variant="h6" fontWeight={400}>
            {search.trim() ? "No matching profiles" : "No scoring profiles yet"}
          </Typography>
          <Typography color="text.secondary">
            {search.trim()
              ? "Try a different search term."
              : "Create a profile to score companies by fit and intent."}
          </Typography>
        </Box>
      ) : isCompact ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 2,
            overflow: "auto",
            pb: 1,
          }}
          data-testid="scoring-profiles-grid"
        >
          {rows.map((profile) => (
            <Paper
              key={profile.id}
              variant="outlined"
              data-testid={`scoring-profile-card-${profile.id}`}
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ wordBreak: "break-word" }}
                >
                  {profile.name}
                </Typography>
                <Box sx={{ flexShrink: 0 }}>{renderActions(profile)}</Box>
              </Box>
              <CriteriaChips profile={profile} />
              <Stack gap={0.5}>
                <MetaRow label="Author" value={profile.email || "—"} />
                <MetaRow
                  label="Created"
                  value={formatDate(profile.createdAt)}
                />
                <MetaRow
                  label="Last Modified"
                  value={formatDate(profile.lastUpdated)}
                />
              </Stack>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
          <Table data-testid="scoring-profiles-table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Scoring criteria</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((profile) => (
                <TableRow
                  key={profile.id}
                  hover
                  data-testid={`scoring-profile-row-${profile.id}`}
                >
                  <TableCell>{profile.name}</TableCell>
                  <TableCell>
                    <CriteriaChips profile={profile} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {profile.email || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatDateFull(profile.createdAt)}>
                      <Typography variant="body2" noWrap>
                        {formatDate(profile.createdAt)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatDateFull(profile.lastUpdated)}>
                      <Typography variant="body2" noWrap>
                        {formatDate(profile.lastUpdated)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ whiteSpace: "nowrap" }}>
                      {renderActions(profile)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {scoringV2Enabled && models.length > 0 && (
        <Box sx={{ mt: 4 }} data-testid="scoring-v2-models-section">
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
            <Typography variant="h6" fontWeight={600}>
              Scoring Models
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Published models, managed outside Market Analyzer (read-only).
            </Typography>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table data-testid="scoring-v2-models-table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Version</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {models.map((model) => (
                  <TableRow
                    key={model.family}
                    data-testid={`scoring-v2-model-row-${model.family}`}
                  >
                    <TableCell>{model.name}</TableCell>
                    <TableCell align="right">v{model.version}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <ScoringWizardDialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        segments={segments}
        onCreateWithAi={() => openAiScoring(getTenantId())}
        onBuildManually={() => {
          setWizardOpen(false);
          setEditingProfile(null);
          setProfileDialogOpen(true);
        }}
        onGenerated={(criteria, name) => {
          setWizardOpen(false);
          setEditingProfile(null);
          setGeneratedCriteria(criteria);
          setGeneratedName(name);
          setProfileDialogOpen(true);
        }}
      />

      <ScoringProfileDialog
        open={profileDialogOpen}
        profile={editingProfile}
        initialCriteria={generatedCriteria}
        initialName={generatedName}
        onClose={() => {
          setProfileDialogOpen(false);
          setGeneratedCriteria(undefined);
          setGeneratedName(undefined);
        }}
        onSaved={() => {
          setProfileDialogOpen(false);
          setGeneratedCriteria(undefined);
          setGeneratedName(undefined);
        }}
      />

      <ConfirmationDialog
        open={deleteDialog.isOpen}
        title="Delete scoring profile"
        description={`Delete "${deleteDialog.data?.name ?? ""}"? This cannot be undone.`}
        okayButtonText={isDeleting ? "Deleting..." : "Delete"}
        cancelButtonText="Cancel"
        onConfirm={handleConfirmDelete}
        onClose={deleteDialog.close}
      />
    </SegmentPageLayout>
  );
};
